import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { projectToWorld } from "./projectToWorld";
import { isBookShelfType, isCircleStairsType, isCircleSymbolType, isPolygonSymbolType, isRectStairsType, isSmartTableType, isSquareSymbolType, isSvgType, isTriangleSymbolType, SymbolType } from "../../types/floorplannerTypes";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { Html, Line } from "@react-three/drei";
import { useDragSelectedObjects } from "../../hooks/useDragSelectedObjects";
import { runInAction, set } from "mobx";
import { observer } from "mobx-react-lite";
import { FaRotate } from "react-icons/fa6";
import { SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";
import { ROTATION_GIZMO_SNAP_STEP } from "../../features/floorplan3d/interaction/symbol3DRotationGizmo";
import { CANVAS_ACCENT_COLOR, canvasHandleFillColor } from "../../constants/brandColors";

const CANVAS_HTML_HANDLE_Z_INDEX_RANGE = [0, 0];

export interface BoundingBox {
  topLeft: [number, number];
  topRight: [number, number];
  bottomLeft: [number, number];
  bottomRight: [number, number];
  width: number;
  height: number;
  depth: number;
}

export interface SelectableSymbolProps {
  symbol: SymbolType;
  children: React.ReactNode;
  handleSize: number;
  calculateBoundingBox: () => BoundingBox;
  onResizeStart?: (event: ThreeEvent<PointerEvent>) => void;
  onResize: (newWidth: number, newHeight: number, boundingBox: BoundingBox, handle: string, newPosition?: [number, number]) => void;
  onResizeEnd?: (event: ThreeEvent<PointerEvent>, finalResize?: { width: number; height: number; handle: string; position?: [number, number] }) => void;
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>) => void;
  center?: [number, number];
  rotation?: number;
  pivot?: [number, number];
  drawHandles?: boolean;
  lockAspectRatio?: boolean;
  onRotate?: (angle: number) => void;
  isDragging?: boolean;
  showHandles?: boolean;
  showSelectionOutline?: boolean;
  showMiddleHandles?: boolean; // Override to show middle handles for specific symbol types
}

/**
 * A component that allows an object to be selected and resized.
 * 
 * @param symbol The symbol to render.
 * @param children The children to render.
 * @param handleSize The size of the handles.
 * @param calculateBoundingBox The function to calculate the bounding box that each symbol use.
 * @param onResizeStart The function to call when the object starts resizing.
 * @param onResize The function to call when the object is resized.
 * @param onResizeEnd The function to call when the object stops resizing.
 * @param center The center of the object.
 * @param rotation The rotation of the object.
 * @param pivot The pivot of the object.
 * @param drawHandles Whether to draw the handles.
 * @param lockAspectRatio Whether to lock the aspect ratio.
 * @param onRotate The function to call when the object is rotated.
 * @returns The SelectableSymbol component.
 * 
 * @see AttachableSymbol
 * @see DraggableObject
 **/
export const SelectableSymbol = observer(React.forwardRef<THREE.Group, SelectableSymbolProps>(
  ({
    symbol,
    children,
    handleSize,
    calculateBoundingBox,
    onResizeStart = () => { },
    onResize,
    onResizeEnd = () => { },
    onPointerOver = () => { },
    onPointerOut = () => { },
    center = [0, 0],
    rotation = 0,
    pivot = [0, 0],
    drawHandles = true,
    lockAspectRatio = true,
    onRotate = () => { }, // Default to no-op
    isDragging = false,
    showHandles = true,
    showSelectionOutline = true,
    showMiddleHandles: showMiddleHandlesPropOverride,
  }, ref) => {
    const [boundingBox, setBoundingBox] = useState<BoundingBox>({
      topLeft: [0, 0],
      topRight: [0, 0],
      bottomLeft: [0, 0],
      bottomRight: [0, 0],
      width: 0,
      height: 0,
      depth: 0,
    });

    const { gl, camera } = useThree();
    // Only selected symbols subscribe to zoom-settling. Reading it on every
    // footprint re-rendered 100+ symbols and invalidated the canvas per wheel tick.
    const needsSelectionChrome = Boolean(symbol.selected && showHandles && drawHandles && !isDragging);
    const selectionHandlesVisible = needsSelectionChrome && editorStore.shouldShowSelectionHandles();
    const singleSelectionOverlayVisible = needsSelectionChrome && editorStore.selections.length === 1;
    const zoomLevelDivisor = selectionHandlesVisible
      ? editorStore.zoomLevelDivisor()
      : 1;
    const handleRadius = handleSize / zoomLevelDivisor;
    const handleDepth = 0.02; // Depth of the handles
    const initialPointerPosition = useRef<[number, number] | null>(null);
    const isResizing = useRef(false);
    const [activeResizeHandle, setActiveResizeHandle] = useState<string | null>(null);
    const [isRotating, setIsRotating] = useState(false);
    const groupRef = useRef<THREE.Group>(null);
    const rotationStart = useRef<number>(rotation);
    const angleRef = useRef<number>(rotation);
    const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);
    const rotatedPivot = new THREE.Vector3(pivot[0], pivot[1], 0).applyMatrix4(new THREE.Matrix4().makeRotationZ(rotation));
    const showMiddleHandlesInternal = isSquareSymbolType(symbol) || isCircleSymbolType(symbol) || isTriangleSymbolType(symbol) || isPolygonSymbolType(symbol);
    const showMiddleHandles = showMiddleHandlesPropOverride ?? showMiddleHandlesInternal;
    const { startDraggingGroupSelection, isDraggingGroupSelection } = useDragSelectedObjects(gl, camera);
    const noopRaycast = () => null;
    const htmlRef = useRef(null);
    const [classHasCursor, setClassHasCursor] = useState<boolean>();
    const aboutToModify = useRef(false);
    const latestResize = useRef<{ width: number; height: number; handle: string; position?: [number, number] } | undefined>(undefined);

    const rotateVector2 = (vector: THREE.Vector2, angle: number): THREE.Vector2 => {
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      return new THREE.Vector2(
        vector.x * cos - vector.y * sin,
        vector.x * sin + vector.y * cos
      );
    };

    const handlePointerDown = (
      event: ThreeEvent<PointerEvent>,
      corner: string
    ) => {
      if (floorplannerStore.lockedByOther) return;
      event.stopPropagation();
      onResizeStart?.(event);
      setActiveResizeHandle(corner);

      // Begin delta-based undo tracking for resize operation
      floorplannerStore.beginUndoTransaction('resizeSymbol');
      floorplannerStore.recordSymbolForUndo(symbol.id);
      const attachedWallAtResizeStart = floorplannerStore.symbolIsAttached(symbol.id);
      if (attachedWallAtResizeStart) {
        floorplannerStore.recordWallForUndo(attachedWallAtResizeStart);
      }

      const initialBoundingBox = calculateBoundingBox();
      const initialAspectRatio = initialBoundingBox.width / initialBoundingBox.height;

      // Helper function to get actual corner position from bounding box
      const getActualCornerLocal = (cornerName: string): THREE.Vector2 => {
        switch (cornerName) {
          case 'topLeft':
            return new THREE.Vector2(initialBoundingBox.topLeft[0], initialBoundingBox.topLeft[1]);
          case 'topRight':
            return new THREE.Vector2(initialBoundingBox.topRight[0], initialBoundingBox.topRight[1]);
          case 'bottomLeft':
            return new THREE.Vector2(initialBoundingBox.bottomLeft[0], initialBoundingBox.bottomLeft[1]);
          case 'bottomRight':
            return new THREE.Vector2(initialBoundingBox.bottomRight[0], initialBoundingBox.bottomRight[1]);
          case 'midRight':
            return new THREE.Vector2(
              initialBoundingBox.topRight[0],
              (initialBoundingBox.topRight[1] + initialBoundingBox.bottomRight[1]) / 2
            );
          case 'midLeft':
            return new THREE.Vector2(
              initialBoundingBox.topLeft[0],
              (initialBoundingBox.topLeft[1] + initialBoundingBox.bottomLeft[1]) / 2
            );
          case 'midTop':
            return new THREE.Vector2(
              (initialBoundingBox.topLeft[0] + initialBoundingBox.topRight[0]) / 2,
              initialBoundingBox.topLeft[1]
            );
          case 'midBottom':
            return new THREE.Vector2(
              (initialBoundingBox.bottomLeft[0] + initialBoundingBox.bottomRight[0]) / 2,
              initialBoundingBox.bottomLeft[1]
            );
          default:
            return new THREE.Vector2(0, 0);
        }
      };

      // Helper function to get corner position relative to center (for symbols that resize symmetrically)
      const getCornerLocalPosition = (cornerName: string, width: number, height: number) => {
        const halfWidth = width / 2;
        const halfHeight = height / 2;
        switch (cornerName) {
          case 'topLeft':
            return new THREE.Vector2(-halfWidth, halfHeight);
          case 'topRight':
            return new THREE.Vector2(halfWidth, halfHeight);
          case 'bottomLeft':
            return new THREE.Vector2(-halfWidth, -halfHeight);
          case 'bottomRight':
            return new THREE.Vector2(halfWidth, -halfHeight);
          case 'midRight':
            return new THREE.Vector2(halfWidth, 0);
          case 'midLeft':
            return new THREE.Vector2(-halfWidth, 0);
          case 'midTop':
            return new THREE.Vector2(0, halfHeight);
          case 'midBottom':
            return new THREE.Vector2(0, -halfHeight);
          default:
            return new THREE.Vector2(0, 0);
        }
      };

      const oppositeHandle = (cornerName: string) => {
        switch (cornerName) {
          case 'topLeft':
            return 'bottomRight';
          case 'topRight':
            return 'bottomLeft';
          case 'bottomLeft':
            return 'topRight';
          case 'bottomRight':
            return 'topLeft';
          case 'midRight':
            return 'midLeft';
          case 'midLeft':
            return 'midRight';
          case 'midTop':
            return 'midBottom';
          case 'midBottom':
            return 'midTop';
          default:
            return '';
        }
      };

      const oppositeCornerName = oppositeHandle(corner);
      const cornerSignMap: Record<'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', { x: number; y: number }> = {
        topLeft: { x: -1, y: 1 },
        topRight: { x: 1, y: 1 },
        bottomLeft: { x: -1, y: -1 },
        bottomRight: { x: 1, y: -1 },
      };
      const isCornerHandle = corner === "topLeft" || corner === "topRight" || corner === "bottomLeft" || corner === "bottomRight";
      const oppositeCornerSign = (oppositeCornerName === "topLeft" || oppositeCornerName === "topRight" || oppositeCornerName === "bottomLeft" || oppositeCornerName === "bottomRight")
        ? cornerSignMap[oppositeCornerName]
        : undefined;
      const minHandleSize = 0.06;
      const initialWidth = initialBoundingBox.width;
      const initialHeight = initialBoundingBox.height;
      const initialSymbolPosition = new THREE.Vector2(
        symbol.position.x,
        symbol.position.y
      );

      // Calculate the derived center from the bounding box (same as used elsewhere)
      const derivedCenterX = (initialBoundingBox.topLeft[0] + initialBoundingBox.bottomRight[0]) / 2;
      const derivedCenterY = (initialBoundingBox.topLeft[1] + initialBoundingBox.bottomRight[1]) / 2;
      const derivedCenterLocal = new THREE.Vector2(derivedCenterX, derivedCenterY);

      // Get the actual opposite corner position from the bounding box
      const oppositeCornerLocal = getActualCornerLocal(oppositeCornerName);

      // Rotate to get world coordinates - the opposite corner should stay fixed during resize
      const derivedCenterRotated = derivedCenterLocal.clone().rotateAround(new THREE.Vector2(0, 0), rotation);
      const oppositeCornerRotated = oppositeCornerLocal.clone().rotateAround(new THREE.Vector2(0, 0), rotation);
      const oppositeCornerWorld = oppositeCornerRotated.clone().add(initialSymbolPosition);

      const [initialWorldX, initialWorldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
      const initialPointerPosition = new THREE.Vector2(initialWorldX, initialWorldY);

      const onPointerMove = (moveEvent: PointerEvent) => {
        runInAction(() => {
          isResizing.current = true;
          const [worldX, worldY] = projectToWorld(
            moveEvent.clientX,
            moveEvent.clientY,
            gl,
            camera
          );
          const currentPointerPosition = new THREE.Vector2(worldX, worldY);
          const pointerDelta = currentPointerPosition.clone().sub(initialPointerPosition);

          // Rotate the pointer delta into the symbol's local coordinate system
          const deltaLocal = rotateVector2(pointerDelta, -rotation);

          let deltaWidth = 0;
          let deltaHeight = 0;

          // Adjust deltaWidth and deltaHeight based on the corner
          if (corner === "topLeft") {
            deltaWidth = -deltaLocal.x;
            deltaHeight = deltaLocal.y;
          } else if (corner === "topRight") {
            deltaWidth = deltaLocal.x;
            deltaHeight = deltaLocal.y;
          } else if (corner === "bottomLeft") {
            deltaWidth = -deltaLocal.x;
            deltaHeight = -deltaLocal.y;
          } else if (corner === "bottomRight") {
            deltaWidth = deltaLocal.x;
            deltaHeight = -deltaLocal.y;
          } else if (corner === "midRight") {
            deltaWidth = deltaLocal.x;
          } else if (corner === "midLeft") {
            deltaWidth = -deltaLocal.x;
          } else if (corner === "midTop") {
            deltaHeight = deltaLocal.y;
          } else if (corner === "midBottom") {
            deltaHeight = -deltaLocal.y;
          }

          // Calculate new width and height (smaller than 0.06 will make grab handles on top of each other)
          let newWidth = Math.max(initialWidth + deltaWidth, minHandleSize);
          let newHeight = Math.max(initialHeight + deltaHeight, minHandleSize);
          let handledByCornerLogic = false;
          let newSymbolPosition: THREE.Vector2 | null = null;

          // Toggle aspect ratio lock when SHIFT is pressed
          // If original lockAspectRatio is true and SHIFT is pressed, effective lock is false (and vice versa)
          const effectiveLockAspectRatio = moveEvent.shiftKey ? !lockAspectRatio : lockAspectRatio;

          if (isCornerHandle && oppositeCornerSign) {
            handledByCornerLogic = true;
            const pointerRelativeToAnchorWorld = currentPointerPosition.clone().sub(oppositeCornerWorld);
            const pointerRelativeToAnchorLocal = rotateVector2(pointerRelativeToAnchorWorld, -rotation);
            let targetWidth = Math.max(Math.abs(pointerRelativeToAnchorLocal.x), minHandleSize);
            let targetHeight = Math.max(Math.abs(pointerRelativeToAnchorLocal.y), minHandleSize);

            if (effectiveLockAspectRatio) {
              const initialRatio = initialHeight !== 0 ? Math.abs(initialWidth / initialHeight) : 1;
              const projectionAxis = new THREE.Vector2(initialRatio, 1);
              const dot = targetWidth * projectionAxis.x + targetHeight * projectionAxis.y;
              const denom = projectionAxis.x * projectionAxis.x + projectionAxis.y * projectionAxis.y;
              const scale = denom !== 0 ? dot / denom : 0;
              const minHeightForRatio = minHandleSize / (initialRatio || 1);
              const projectedHeight = Math.max(scale, minHeightForRatio);
              targetHeight = projectedHeight;
              targetWidth = Math.max(projectedHeight * initialRatio, minHandleSize);
            }

            newWidth = targetWidth;
            newHeight = targetHeight;

            if (
              isSvgType(symbol) ||
              isSmartTableType(symbol) ||
              isBookShelfType(symbol) ||
              isTriangleSymbolType(symbol) ||
              isSquareSymbolType(symbol) ||
              isCircleSymbolType(symbol) ||
              isPolygonSymbolType(symbol) ||
              isRectStairsType(symbol)
              // Note: CircleStairs is excluded - its hinge at (0,0) should stay fixed during resize
            ) {
              const anchorOffsetLocal = new THREE.Vector2(
                oppositeCornerSign.x * newWidth / 2,
                oppositeCornerSign.y * newHeight / 2
              );
              const anchorOffsetWorld = rotateVector2(anchorOffsetLocal, rotation);
              newSymbolPosition = oppositeCornerWorld.clone().sub(anchorOffsetWorld);
            }
          }

          if (!handledByCornerLogic && effectiveLockAspectRatio) {
            if (Math.abs(deltaWidth) > Math.abs(deltaHeight)) {
              newHeight = newWidth / initialAspectRatio;
            } else {
              newWidth = newHeight * initialAspectRatio;
            }
          }

          if (!handledByCornerLogic &&
            (isSvgType(symbol) ||
              isSmartTableType(symbol) ||
              isBookShelfType(symbol) ||
              isTriangleSymbolType(symbol) ||
              isSquareSymbolType(symbol) ||
              isCircleSymbolType(symbol) ||
              isPolygonSymbolType(symbol) ||
              isRectStairsType(symbol))) {
            // Note: CircleStairs is excluded - its hinge at (0,0) should stay fixed during resize
            newSymbolPosition = new THREE.Vector2(initialSymbolPosition.x, initialSymbolPosition.y);

            // Get the new local position of the opposite corner after resizing
            const newOppositeCornerLocal = getCornerLocalPosition(oppositeCornerName, newWidth, newHeight);

            // Rotate to get new world coordinates
            const newOppositeCornerWorld = newOppositeCornerLocal
              .clone()
              .rotateAround(new THREE.Vector2(0, 0), rotation)
              .add(newSymbolPosition);

            // Compute the delta between the initial and new opposite corner positions
            const oppositeCornerDelta = oppositeCornerWorld.clone().sub(newOppositeCornerWorld);

            // Adjust the symbol's position
            newSymbolPosition.add(oppositeCornerDelta);
          }
          const nextPosition = newSymbolPosition
            ? [newSymbolPosition.x, newSymbolPosition.y] as [number, number]
            : undefined;
          latestResize.current = {
            width: newWidth,
            height: newHeight,
            handle: corner,
            position: nextPosition,
          };

          // Propagate the resize to the consumer
          onResize(newWidth, newHeight, initialBoundingBox, corner, nextPosition);

          if (newSymbolPosition && floorplannerStore.symbolIsAttached(symbol.id)) {
            floorplannerStore.updateSymbolPositionRespectingAttachment(
              symbol.id,
              [newSymbolPosition.x, newSymbolPosition.y],
            );
          }
        });
      };

      const onPointerUp = (upEvent: PointerEvent) => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);

        floorplannerStore.setBlockDirty(false);
        setHoveredHandle(null);
        setActiveResizeHandle(null);
        isResizing.current = false;

        onResizeEnd?.(event, latestResize.current);
        latestResize.current = undefined;

        // Complete delta-based undo tracking after resize consumers commit their final store values.
        const attachedWall = floorplannerStore.symbolIsAttached(symbol.id) ?? attachedWallAtResizeStart;
        if (attachedWall) {
          floorplannerStore.recordWallChange(attachedWall, 'modify');
        }
        floorplannerStore.recordSymbolChange(symbol.id, 'modify');
        floorplannerStore.pushToUndoStack();
        floorplannerStore.setDirty();

        document.body.style.cursor = 'auto';
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      floorplannerStore.setBlockDirty(true);
    };


    const handleGroupPointerDown = (event: ThreeEvent<PointerEvent>) => {
      if (floorplannerStore.lockedByOther) {
        event.stopPropagation();
        if (!editorStore.isShiftPressed) {
          editorStore.clearSelections();
        }
        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
        return;
      }
      // Do not interact if in construction mode
      if (editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || editorStore.rulerConstructionMode || editorStore.wallConstructionMode || editorStore.lineConstructionMode) {
        return;
      }

      // Check if this object is part of a group and if the group is not fully selected
      let shouldSelectGroup = false;
      if (!symbol.selected) {
        const groupId = floorplannerStore.getObjectGroup(symbol.id);
        if (groupId) {
          // Check if all group members are selected
          const groupObjects = floorplannerStore.getGroupObjects(groupId);
          const allGroupSelected = groupObjects.every(obj =>
            editorStore.selections.some(sel => sel.id === obj.id)
          );
          shouldSelectGroup = !allGroupSelected;
        }
      }

      // Select the group if needed
      if (shouldSelectGroup && !symbol.selected) {
        if (!editorStore.isShiftPressed) {
          editorStore.clearSelections();
        }
        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
        // After selecting the group, immediately start group dragging
        startDraggingGroupSelection(event);
        return;
      }

      // If we now have multiple selections, check if this object is part of the selection
      if (editorStore.selections.length > 1) {
        // Only use group drag if this symbol is part of the current selection
        if (symbol.selected) {
          startDraggingGroupSelection(event);
          return;
        }
        // If this symbol is not selected, continue with normal selection logic below
      }

      initialPointerPosition.current = [event.clientX, event.clientY];
      isResizing.current = false;
      setHoveredHandle(null);

      const onPointerUp = (upEvent: PointerEvent) => {
        const [initialX, initialY] = initialPointerPosition.current!;
        const movedX = Math.abs(upEvent.clientX - initialX);
        const movedY = Math.abs(upEvent.clientY - initialY);

        if (movedX < 5 && movedY < 5 && !isResizing.current) {
          // Select the object if it was not dragged
          if (!editorStore.isShiftPressed) {
            editorStore.clearSelections();
          }
          editorStore.addSelection(symbol);
          floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
        }

        window.removeEventListener("pointerup", onPointerUp);
        floorplannerStore.setBlockDirty(false);
        floorplannerStore.setDirty();
      };

      window.addEventListener("pointerup", onPointerUp);
      floorplannerStore.setBlockDirty(true);
    };

    const handleRotatePointerDown = (handle: string, event: ThreeEvent<PointerEvent>) => {
      if (floorplannerStore.lockedByOther) return;
      event.stopPropagation();
      aboutToModify.current = true;
      const [initialWorldX, initialWorldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
      const initialPointer = new THREE.Vector2(initialWorldX, initialWorldY);

      // Use derived center from bounding box for proper center-based rotation
      const derivedCenterX = (boundingBox.topLeft[0] + boundingBox.bottomRight[0]) / 2;
      const derivedCenterY = (boundingBox.topLeft[1] + boundingBox.bottomRight[1]) / 2;
      const localCenter = new THREE.Vector2(derivedCenterX, derivedCenterY);

      // Calculate the initial world center position
      const initialRotation = rotation;
      const rotatedLocalCenter = localCenter.clone().rotateAround(new THREE.Vector2(0, 0), initialRotation);
      const initialSymbolPosition = new THREE.Vector2(symbol.position.x, symbol.position.y);
      const initialWorldCenter = initialSymbolPosition.clone().add(rotatedLocalCenter).add(new THREE.Vector2(rotatedPivot.x, rotatedPivot.y));

      floorplannerStore.setBlockDirty(true);
      const attachedWallAtRotateStart = floorplannerStore.symbolIsAttached(symbol.id);

      let rotateRafId: number | null = null;
      let pendingRotateEvent: PointerEvent | null = null;

      const processPointerMove = (moveEvent: PointerEvent) => {
        if (aboutToModify.current) {
          aboutToModify.current = false;
          // Begin delta-based undo tracking for rotation
          floorplannerStore.beginUndoTransaction('rotateSymbol');
          floorplannerStore.recordSymbolForUndo(symbol.id);
          if (attachedWallAtRotateStart) {
            floorplannerStore.recordWallForUndo(attachedWallAtRotateStart);
          }
        }
        setIsRotating(true);
        // Convert the clientX/clientY (screen coordinates) to world coordinates using the projectToWorld function
        const [worldX, worldY] = projectToWorld(moveEvent.clientX, moveEvent.clientY, gl, camera);

        // Current pointer position in world coordinates
        const currentPointer = new THREE.Vector2(worldX, worldY);

        // Calculate the distance between the current pointer and the symbol's center
        const distanceToCenter = currentPointer.distanceTo(initialWorldCenter);

        // Define a threshold distance to avoid jumpy behavior near the center
        const threshold = 0.1;

        // If the pointer is too close to the symbol's center, ignore angle changes to prevent jumping
        if (distanceToCenter < threshold) {
          return; // Skip angle update if the pointer is too close to the center
        }

        // Calculate the initial and current angles relative to the symbol's center (when the user starts dragging and moves the mouse)
        const initialAngle = Math.atan2(initialPointer.y - initialWorldCenter.y, initialPointer.x - initialWorldCenter.x);
        const currentAngle = Math.atan2(currentPointer.y - initialWorldCenter.y, currentPointer.x - initialWorldCenter.x);

        // Calculate the delta angle (change in angle) only if the angle has changed
        let deltaAngle = currentAngle - initialAngle;

        // Ignore mouse movements that don't change the angle (i.e., pure radial movement)
        if (deltaAngle === 0) {
          return; // No change in angle, no rotation needed
        }

        // Handle angle wrapping to ensure smooth rotation across ±π boundaries
        if (deltaAngle > Math.PI) {
          deltaAngle -= 2 * Math.PI;
        } else if (deltaAngle < -Math.PI) {
          deltaAngle += 2 * Math.PI;
        }

        // Compute the new rotation angle in radians
        let newAngle = rotationStart.current + deltaAngle;

        // Match 3D rotation: Shift snaps to 45° increments, otherwise free rotation
        if (editorStore.isShiftPressed) {
          newAngle =
            Math.round(newAngle / ROTATION_GIZMO_SNAP_STEP) *
            ROTATION_GIZMO_SNAP_STEP;
        }

        // Calculate the new position to keep the center stationary
        // 1. Rotate the local center by the new angle
        const newRotatedLocalCenter = localCenter.clone().rotateAround(new THREE.Vector2(0, 0), newAngle);
        // 2. The new position is: worldCenter - newRotatedLocalCenter - rotatedPivot (at new angle)
        const newRotatedPivot = new THREE.Vector2(pivot[0], pivot[1]).rotateAround(new THREE.Vector2(0, 0), newAngle);
        const newPosition = initialWorldCenter.clone()
          .sub(newRotatedLocalCenter)
          .sub(newRotatedPivot);

        // Update the angle reference and trigger the rotation callback
        angleRef.current = newAngle;
        onRotate(newAngle);

        // Update the symbol's rotation and position in the store
        const movedAttachedSymbol = floorplannerStore.updateSymbolPositionRespectingAttachment(
          symbol.id,
          [newPosition.x, newPosition.y],
        );
        if (!movedAttachedSymbol) {
          floorplannerStore.updateSymbolProperty(symbol.id, "rotation", angleRef.current);
        }
      };

      const flushPendingRotation = () => {
        if (rotateRafId !== null) {
          cancelAnimationFrame(rotateRafId);
          rotateRafId = null;
        }

        const pending = pendingRotateEvent;
        pendingRotateEvent = null;
        if (pending) {
          processPointerMove(pending);
        }
      };

      const onPointerMove = (moveEvent: PointerEvent) => {
        pendingRotateEvent = moveEvent;
        if (rotateRafId !== null) {
          return;
        }

        rotateRafId = requestAnimationFrame(() => {
          rotateRafId = null;
          const pending = pendingRotateEvent;
          pendingRotateEvent = null;
          if (pending) {
            processPointerMove(pending);
          }
        });
      };

      const onPointerUp = () => {
        flushPendingRotation();
        aboutToModify.current = false;

        // Complete delta-based undo tracking for rotation
        if (!aboutToModify.current) {
          const attachedWall = floorplannerStore.symbolIsAttached(symbol.id) ?? attachedWallAtRotateStart;
          if (attachedWall) {
            floorplannerStore.recordWallChange(attachedWall, 'modify');
          }
          floorplannerStore.recordSymbolChange(symbol.id, 'modify');
          floorplannerStore.pushToUndoStack();
        }

        setIsRotating(false);
        floorplannerStore.setBlockDirty(false);
        floorplannerStore.setDirty();
        // Remove event listeners
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        rotationStart.current = angleRef.current;

        // Force a re-render (invalidate() does not work inside an event)
        setBoundingBox(calculateBoundingBox());
        document.body.style.cursor = 'auto';
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    };

    // Define the cursor for each handle based on the current rotation
    const cursorMapToQuadant = {
      topLeft: ["nwse-resize", "nesw-resize", "nwse-resize", "nesw-resize"],
      topRight: ["nesw-resize", "nwse-resize", "nesw-resize", "nwse-resize"],
      bottomRight: ["nwse-resize", "nesw-resize", "nwse-resize", "nesw-resize"],
      bottomLeft: ["nesw-resize", "nwse-resize", "nesw-resize", "nwse-resize"],
      midTop: ["ns-resize", "ew-resize", "ns-resize", "ew-resize"],
      midBottom: ["ns-resize", "ew-resize", "ns-resize", "ew-resize"],
      midLeft: ["ew-resize", "ns-resize", "ew-resize", "ns-resize"],
      midRight: ["ew-resize", "ns-resize", "ew-resize", "ns-resize"],
    };

    const handlePointerEnter = (handle: keyof typeof cursorMapToQuadant) => {
      const normalizedRotation = (rotation % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2); // Normalize rotation within [0, 2*PI)
      const quadrant = Math.floor((normalizedRotation + Math.PI / 4) / (Math.PI / 2)) % 4; // Adjust quadrant for a smoother transition

      // Set the cursor based on the handle and the current quadrant
      document.body.style.cursor = cursorMapToQuadant[handle][quadrant] || "auto";

      setHoveredHandle(handle);
    };

    const handlePointerLeave = () => {
      document.body.style.cursor = "auto";
      setHoveredHandle(null);
    };

    const rotationMatrix = new THREE.Matrix4().makeRotationZ(rotation);
    // Calculate center from bounding box to ensure synchronization with the selection rectangle
    // This avoids timing issues where the center prop might not yet reflect the updated bounding box
    const derivedCenterX = (boundingBox.topLeft[0] + boundingBox.bottomRight[0]) / 2;
    const derivedCenterY = (boundingBox.topLeft[1] + boundingBox.bottomRight[1]) / 2;
    const transformedCenter = new THREE.Vector3(derivedCenterX, derivedCenterY, 0).applyMatrix4(rotationMatrix);

    const getTransformedCorner = (x: number, y: number) => {
      const corner = new THREE.Vector3(x, y, 0);
      corner.applyMatrix4(rotationMatrix);
      return corner;
    };

    const topLeft = getTransformedCorner(boundingBox.topLeft[0], boundingBox.topLeft[1]);
    const topRight = getTransformedCorner(boundingBox.topRight[0], boundingBox.topRight[1]);
    const bottomLeft = getTransformedCorner(boundingBox.bottomLeft[0], boundingBox.bottomLeft[1]);
    const bottomRight = getTransformedCorner(boundingBox.bottomRight[0], boundingBox.bottomRight[1]);

    const isHovered = (handle: string) => hoveredHandle === handle && !activeResizeHandle;
    // Returns true if the handle should be filled (either hovered when not resizing, or actively being dragged)
    const isHandleFilled = (handle: string) => activeResizeHandle === handle || (hoveredHandle === handle && !activeResizeHandle);
    // Calculate midpoints
    const midLeft = new THREE.Vector3(
      (topLeft.x + bottomLeft.x) / 2 + rotatedPivot.x,
      (topLeft.y + bottomLeft.y) / 2 + rotatedPivot.y,
      0
    );

    const midRight = new THREE.Vector3(
      (topRight.x + bottomRight.x) / 2 + rotatedPivot.x,
      (topRight.y + bottomRight.y) / 2 + rotatedPivot.y,
      0
    );
    const midTop = new THREE.Vector3(
      (topLeft.x + topRight.x) / 2 + rotatedPivot.x,
      (topLeft.y + topRight.y) / 2 + rotatedPivot.y,
      0
    );

    const midBottom = new THREE.Vector3(
      (bottomLeft.x + bottomRight.x) / 2 + rotatedPivot.x,
      (bottomLeft.y + bottomRight.y) / 2 + rotatedPivot.y,
      0
    );

    // Define the distance from the top center to the rotation handle
    const rotationHandleDistance = 0.28 / zoomLevelDivisor;

    // Calculate the upward vector based on the symbol's rotation
    const upVector = new THREE.Vector3(0, 1, 0)
      .applyQuaternion(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), rotation));

    // Calculate the rotation handle position
    const rotationHandlePos = new THREE.Vector3(
      midTop.x + rotatedPivot.x + upVector.x * rotationHandleDistance,
      midTop.y + rotatedPivot.y + upVector.y * rotationHandleDistance,
      0
    );
    const resizeHandleHitRadius = Math.max(handleRadius, 0.075 / zoomLevelDivisor);
    const renderResizeHandleHitTarget = () => (
      <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
        <circleGeometry args={[resizeHandleHitRadius, 24]} />
        <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
      </mesh>
    );

    // Bounding boxes are in world units and do not depend on camera zoom.
    useEffect(() => {
      if (groupRef.current) {
        const boundingBox = calculateBoundingBox();
        setBoundingBox(boundingBox);
      }

    }, [symbol.position, symbol.rotation, isResizing.current, isRotating]);

    useLayoutEffect(() => {
      if (groupRef.current) {
        const newBoundingBox = calculateBoundingBox();
        setBoundingBox(newBoundingBox);
      }
    }, [symbol.position, symbol.rotation, calculateBoundingBox, rotation]);

    useEffect(() => {
      return () => {
        document.body.style.cursor = 'auto';
        document.body.classList.remove("cursor-vector");
      };
    }, []);

    return (
      <group ref={groupRef} onClick={(event) => event.stopPropagation()}>
        {/* Draw a transparent plane to capture pointer events */}
        {Number.isFinite(boundingBox.width) && Number.isFinite(boundingBox.height) && boundingBox.width > 0 && boundingBox.height > 0 && (
          <mesh
            position={
              [
                transformedCenter.x + rotatedPivot.x,
                transformedCenter.y + rotatedPivot.y,
                -0.001,
              ]
            }
            rotation={[0, 0, rotation]}
            onPointerDown={handleGroupPointerDown}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
          >
            <planeGeometry args={[boundingBox.width, boundingBox.height]} />
            <meshBasicMaterial

              color={symbol.selected ? "yellow" : "white"}
              transparent
              opacity={0}
            // color={symbol.selected ? "yellow" : "white"}
            />
          </mesh>
        )}
        {children}
        {(selectionHandlesVisible && singleSelectionOverlayVisible && !isResizing.current && !floorplannerStore.symbolIsAttached(symbol.id) && (
          <>
            {/* Rotation Handle */}
            <Line
              points={[
                new THREE.Vector3(midTop.x, midTop.y, 0),
                rotationHandlePos.sub(rotatedPivot),
              ]}
              color="gray"
              linewidth={1}
              raycast={noopRaycast}
            />
            <group
              position={[rotationHandlePos.x, rotationHandlePos.y, SELECTED_HANDLE_INTERACTION_Z]}
              onPointerDown={(e) => handleRotatePointerDown("midTop", e)}
              onPointerEnter={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
              }}
              onPointerLeave={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
              }}
            >
              {/* Transparent plane to capture events */}
              {(Number.isFinite(zoomLevelDivisor) && zoomLevelDivisor > 0) && (
                <mesh
                  position={[0, 0, 0]} // Align with your Html content
                >
                  <planeGeometry args={[0.3 / zoomLevelDivisor, 0.3 / zoomLevelDivisor]} />
                  <meshBasicMaterial
                    //color={"green"}
                    transparent opacity={0}
                  />
                </mesh>
              )}
              {/* Use Html component from drei to render the FaRotate icon */}
              <Html
                ref={htmlRef}
                center
                zIndexRange={CANVAS_HTML_HANDLE_Z_INDEX_RANGE}
                style={{
                  pointerEvents: "none",
                  transform: `rotate(${-THREE.MathUtils.radToDeg(symbol.rotation as number)}deg)`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "fit-content",
                    height: "fit-content",
                  }}
                >
                  <FaRotate size={18} color={"black"} />
                </div>
              </Html>
            </group>
          </>
        ))}
        {/* Selection box outline - visible during resize for visual feedback */}
        {(showHandles && showSelectionOutline && singleSelectionOverlayVisible && !isRotating) && (
          <>
            {/* Draw a thin blue line between all four corners (Using Line of drei) */}
            <Line
              points={[topLeft.clone().add(new THREE.Vector3(
                rotatedPivot.x,
                rotatedPivot.y,
                0)), topRight.clone().add(new THREE.Vector3(
                  rotatedPivot.x,
                  rotatedPivot.y,
                  0))]}
              color={CANVAS_ACCENT_COLOR}
              linewidth={0.4}
              raycast={noopRaycast}
            />
            <Line
              points={[topRight.clone().add(new THREE.Vector3(
                rotatedPivot.x,
                rotatedPivot.y,
                0)), bottomRight.clone().add(new THREE.Vector3(
                  rotatedPivot.x,
                  rotatedPivot.y,
                  0))]}
              color={CANVAS_ACCENT_COLOR}
              linewidth={0.4}
              raycast={noopRaycast}
            />
            <Line
              points={[bottomRight.clone().add(new THREE.Vector3(
                rotatedPivot.x,
                rotatedPivot.y,
                0)), bottomLeft.clone().add(new THREE.Vector3(
                  rotatedPivot.x,
                  rotatedPivot.y,
                  0))]}
              color={CANVAS_ACCENT_COLOR}
              linewidth={0.4}
              raycast={noopRaycast}
            />
            <Line
              points={[bottomLeft.clone().add(new THREE.Vector3(
                rotatedPivot.x,
                rotatedPivot.y,
                0)), topLeft.clone().add(new THREE.Vector3(
                  rotatedPivot.x,
                  rotatedPivot.y,
                  0))]}
              color={CANVAS_ACCENT_COLOR}
              linewidth={0.4}
              raycast={noopRaycast}
            />
          </>
        )}
        {/* Resize handles - visible during resize, active handle is filled */}
        {(selectionHandlesVisible && singleSelectionOverlayVisible && !isRotating) && (
          <>
            <group
              position={[
                topRight.x + rotatedPivot.x,
                topRight.y + rotatedPivot.y,
                SELECTED_HANDLE_INTERACTION_Z,
              ]}
              renderOrder={SELECTED_HANDLE_RENDER_ORDER}
              onPointerDown={(e) => handlePointerDown(e, "topRight")}
              onPointerOver={() => handlePointerEnter("topRight")}
              onPointerOut={handlePointerLeave}
            >
              {renderResizeHandleHitTarget()}
              <mesh>
                <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
              </mesh>
              <mesh
                position={[0, 0, handleDepth / 2]}
                rotation={[Math.PI / 2, 0, 0]}
              >
                <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "topRight", isHovered("topRight"))} />
              </mesh>
            </group>


            {/* Top Left Handle */}
            <group
              position={[
                topLeft.x + rotatedPivot.x,
                topLeft.y + rotatedPivot.y,
                SELECTED_HANDLE_INTERACTION_Z,
              ]}
              renderOrder={SELECTED_HANDLE_RENDER_ORDER}
              onPointerDown={(e) => handlePointerDown(e, "topLeft")}
              onPointerOver={() => handlePointerEnter("topLeft")}
              onPointerOut={handlePointerLeave}
            >
              {renderResizeHandleHitTarget()}
              <mesh>
                <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
              </mesh>
              <mesh
                position={[0, 0, handleDepth / 2]}
                rotation={[Math.PI / 2, 0, 0]}
              >
                <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "topLeft", isHovered("topLeft"))} />
              </mesh>
            </group>

            {/* Bottom Right Handle */}
            <group
              position={[
                bottomRight.x + rotatedPivot.x,
                bottomRight.y + rotatedPivot.y,
                SELECTED_HANDLE_INTERACTION_Z,
              ]}
              renderOrder={SELECTED_HANDLE_RENDER_ORDER}
              onPointerDown={(e) => handlePointerDown(e, "bottomRight")}
              onPointerOver={() => handlePointerEnter("bottomRight")}
              onPointerOut={handlePointerLeave}
            >
              {renderResizeHandleHitTarget()}
              <mesh>
                <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
              </mesh>
              <mesh
                position={[0, 0, handleDepth / 2]}
                rotation={[Math.PI / 2, 0, 0]}
              >
                <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "bottomRight", isHovered("bottomRight"))} />
              </mesh>
            </group>

            {/* Bottom Left Handle */}
            <group
              position={[
                bottomLeft.x + rotatedPivot.x,
                bottomLeft.y + rotatedPivot.y,
                SELECTED_HANDLE_INTERACTION_Z,
              ]}
              renderOrder={SELECTED_HANDLE_RENDER_ORDER}
              onPointerDown={(e) => handlePointerDown(e, "bottomLeft")}
              onPointerOver={() => handlePointerEnter("bottomLeft")}
              onPointerOut={handlePointerLeave}
            >
              {renderResizeHandleHitTarget()}
              <mesh>
                <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
              </mesh>
              <mesh
                position={[0, 0, handleDepth / 2]}
                rotation={[Math.PI / 2, 0, 0]}
              >
                <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "bottomLeft", isHovered("bottomLeft"))} />
              </mesh>
            </group>

            {showMiddleHandles && (
              <>
                {/* Middle Right Handle */}
                <group
                  position={[midRight.x, midRight.y, SELECTED_HANDLE_INTERACTION_Z]}
                  renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                  onPointerDown={(e) => handlePointerDown(e, "midRight")}
                  onPointerOver={() => handlePointerEnter("midRight")}
                  onPointerOut={handlePointerLeave}
                >
                  {renderResizeHandleHitTarget()}
                  <mesh>
                    <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "midRight", isHovered("midRight"))} />
                  </mesh>
                </group>

                {/* Middle Left Handle */}
                <group
                  position={[midLeft.x, midLeft.y, SELECTED_HANDLE_INTERACTION_Z]}
                  renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                  onPointerDown={(e) => handlePointerDown(e, "midLeft")}
                  onPointerOver={() => handlePointerEnter("midLeft")}
                  onPointerOut={handlePointerLeave}
                >
                  {renderResizeHandleHitTarget()}
                  <mesh>
                    <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "midLeft", isHovered("midLeft"))} />
                  </mesh>
                </group>

                {/* Middle Top Handle */}
                <group
                  position={[midTop.x, midTop.y, SELECTED_HANDLE_INTERACTION_Z]}
                  renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                  onPointerDown={(e) => handlePointerDown(e, "midTop")}
                  onPointerOver={() => handlePointerEnter("midTop")}
                  onPointerOut={handlePointerLeave}
                >
                  {renderResizeHandleHitTarget()}
                  <mesh>
                    <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "midTop", isHovered("midTop"))} />
                  </mesh>
                </group>

                {/* Middle Bottom Handle */}
                <group
                  position={[midBottom.x, midBottom.y, SELECTED_HANDLE_INTERACTION_Z]}
                  renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                  onPointerDown={(e) => handlePointerDown(e, "midBottom")}
                  onPointerOver={() => handlePointerEnter("midBottom")}
                  onPointerOut={handlePointerLeave}
                >
                  {renderResizeHandleHitTarget()}
                  <mesh>
                    <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(activeResizeHandle === "midBottom", isHovered("midBottom"))} />
                  </mesh>
                </group>
              </>
            )}

          </>
        )}
      </group>
    );
  }
));
